import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import { projectsApi, tasksApi, Project, ProjectStatus, Task } from '@shared/api-client'

const statusColors: Record<ProjectStatus, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  planning: 'default',
  active: 'primary',
  on_hold: 'warning',
  completed: 'success',
  cancelled: 'error',
}

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      loadProject()
      loadTasks()
      loadStats()
    }
  }, [id])

  const loadProject = async () => {
    try {
      const data = await projectsApi.get(Number(id))
      setProject(data)
    } catch (error) {
      console.error('Failed to load project:', error)
      alert('Failed to load project')
    } finally {
      setLoading(false)
    }
  }

  const loadTasks = async () => {
    try {
      const data = await tasksApi.list({ project_id: Number(id) })
      setTasks(data)
    } catch (error) {
      console.error('Failed to load tasks:', error)
    }
  }

  const loadStats = async () => {
    try {
      const data = await projectsApi.getStats(Number(id))
      setStats(data)
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString()
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  if (!project) {
    return <Typography>Project not found</Typography>
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
        >
          Back to Projects
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/projects/${id}/edit`)}
        >
          Edit Project
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">{project.name}</Typography>
          <Chip
            label={project.status}
            color={statusColors[project.status]}
          />
        </Box>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {project.description || 'No description'}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography><strong>Priority:</strong> {project.priority}</Typography>
                <Typography><strong>Start Date:</strong> {formatDate(project.start_date)}</Typography>
                <Typography><strong>End Date:</strong> {formatDate(project.end_date)}</Typography>
                <Typography><strong>Budget:</strong> {project.budget ? `$${parseFloat(project.budget).toLocaleString()}` : '-'}</Typography>
                <Typography><strong>Actual Cost:</strong> {project.actual_cost ? `$${parseFloat(project.actual_cost).toLocaleString()}` : '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Progress
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2">{project.progress}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={project.progress} />
                </Box>
                {stats && (
                  <>
                    <Typography><strong>Total Tasks:</strong> {stats.total_tasks}</Typography>
                    {Object.entries(stats.tasks_by_status || {}).map(([status, count]) => (
                      <Typography key={status}>
                        <strong>{status}:</strong> {count as number}
                      </Typography>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Tasks ({tasks.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No tasks found
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => (
                  <TableRow
                    key={task.id}
                    hover
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{task.id}</TableCell>
                    <TableCell>{task.title}</TableCell>
                    <TableCell>
                      <Chip label={task.status} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={task.priority} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(task.due_date)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}

